import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import {
  Speed as GaugeIcon,
  ThreeDRotation as Scatter3DIcon,
  WaterDrop as LiquidIcon,
  Radar as RadarIcon,
  Timeline as TimelineIcon,
  CandlestickChart as CandlestickIcon,
  ShowChart as LineIcon,
  DonutLarge as DonutIcon,
  GridOn as HeatmapIcon,
  AutoGraph as GraphIcon,
} from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';
import 'echarts-gl';
import 'echarts-wordcloud';

const echartsColors = {
  primary: '#0078D4',
  secondary: '#40E0D0',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  orange: '#FF8C00',
  pink: '#E91E63',
  teal: '#009688',
  indigo: '#3F51B5',
  gradient: {
    blue: ['#667eea', '#764ba2'],
    sunset: ['#f093fb', '#f5576c'],
    ocean: ['#2E3192', '#1BFFFF'],
    forest: ['#134E5E', '#71B280'],
  },
  chart: [
    '#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438',
    '#FF8C00', '#E91E63', '#009688', '#3F51B5', '#00BCD4', '#8BC34A',
  ],
};

const ChartCard = ({ title, subtitle, children, icon: Icon, gradientColors }) => {
  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        background: gradientColors 
          ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.05)} 0%, ${alpha(gradientColors[1], 0.05)} 100%)`
          : '#FFFFFF',
        borderRadius: 3,
        border: `1px solid ${alpha(echartsColors.primary, 0.12)}`,
        boxShadow: '0 8px 32px rgba(0, 120, 212, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientColors
            ? `linear-gradient(90deg, ${gradientColors[0]}, ${gradientColors[1]})`
            : `linear-gradient(90deg, ${echartsColors.primary}, ${echartsColors.secondary})`,
        },
        '&:hover': {
          boxShadow: '0 16px 48px rgba(0, 120, 212, 0.12)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: gradientColors
                ? `linear-gradient(135deg, ${alpha(gradientColors[0], 0.15)}, ${alpha(gradientColors[1], 0.15)})`
                : `linear-gradient(135deg, ${alpha(echartsColors.primary, 0.15)}, ${alpha(echartsColors.secondary, 0.15)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 24, color: gradientColors?.[0] || echartsColors.primary }} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};

const EChartsShowcase = () => {
  // Gauge: Student Satisfaction
  const gaugeOption = {
    series: [
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 100,
        splitNumber: 10,
        itemStyle: {
          color: echartsColors.primary,
        },
        progress: {
          show: true,
          width: 18,
        },
        pointer: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            width: 18,
          },
        },
        axisTick: {
          distance: -30,
          splitNumber: 5,
          lineStyle: {
            width: 2,
            color: '#999',
          },
        },
        splitLine: {
          distance: -40,
          length: 14,
          lineStyle: {
            width: 3,
            color: '#999',
          },
        },
        axisLabel: {
          distance: -20,
          color: '#999',
          fontSize: 14,
        },
        anchor: {
          show: false,
        },
        title: {
          show: false,
        },
        detail: {
          valueAnimation: true,
          width: '60%',
          lineHeight: 40,
          borderRadius: 8,
          offsetCenter: [0, '-15%'],
          fontSize: 40,
          fontWeight: 'bold',
          formatter: '{value}%',
          color: 'inherit',
        },
        data: [
          {
            value: 78,
            name: 'NPS Score',
          },
        ],
      },
    ],
  };

  // Multiple Gauges: KPIs Dashboard
  const multiGaugeOption = {
    series: [
      {
        type: 'gauge',
        center: ['25%', '50%'],
        radius: '60%',
        startAngle: 220,
        endAngle: -40,
        min: 0,
        max: 100,
        splitNumber: 5,
        itemStyle: {
          color: echartsColors.success,
        },
        progress: {
          show: true,
        },
        pointer: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            width: 15,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          valueAnimation: true,
          formatter: '{value}%',
          fontSize: 20,
          fontWeight: 'bold',
          offsetCenter: [0, '70%'],
          color: echartsColors.success,
        },
        title: {
          fontSize: 14,
          offsetCenter: [0, '-100%'],
        },
        data: [
          {
            value: 88,
            name: 'Completion Rate',
          },
        ],
      },
      {
        type: 'gauge',
        center: ['75%', '50%'],
        radius: '60%',
        startAngle: 220,
        endAngle: -40,
        min: 0,
        max: 100,
        splitNumber: 5,
        itemStyle: {
          color: echartsColors.warning,
        },
        progress: {
          show: true,
        },
        pointer: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            width: 15,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          valueAnimation: true,
          formatter: '{value}%',
          fontSize: 20,
          fontWeight: 'bold',
          offsetCenter: [0, '70%'],
          color: echartsColors.warning,
        },
        title: {
          fontSize: 14,
          offsetCenter: [0, '-100%'],
        },
        data: [
          {
            value: 72,
            name: 'Engagement',
          },
        ],
      },
    ],
  };

  // Liquid Fill: Course Progress
  const liquidOption = {
    series: [
      {
        type: 'gauge',
        startAngle: 0,
        endAngle: 360,
        radius: '80%',
        center: ['50%', '50%'],
        axisLine: {
          show: true,
          lineStyle: {
            width: 20,
            color: [
              [0.65, alpha(echartsColors.primary, 0.2)],
              [1, echartsColors.primary],
            ],
          },
        },
        pointer: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          formatter: '{value}%\nCompleted',
          fontSize: 24,
          fontWeight: 'bold',
          color: echartsColors.primary,
          offsetCenter: [0, 0],
        },
        data: [
          {
            value: 65,
          },
        ],
      },
    ],
  };

  // Scatter 3D: Student Performance Distribution
  const scatter3DData = [];
  for (let i = 0; i < 100; i++) {
    scatter3DData.push([
      Math.random() * 100, // Attendance
      Math.random() * 100, // Assignment Score
      Math.random() * 100, // Final Exam
    ]);
  }

  const scatter3DOption = {
    tooltip: {},
    visualMap: {
      show: true,
      dimension: 2,
      min: 0,
      max: 100,
      inRange: {
        color: [
          echartsColors.danger,
          echartsColors.warning,
          echartsColors.success,
        ],
      },
    },
    xAxis3D: {
      name: 'Attendance %',
      type: 'value',
    },
    yAxis3D: {
      name: 'Assignments',
      type: 'value',
    },
    zAxis3D: {
      name: 'Final Exam',
      type: 'value',
    },
    grid3D: {
      viewControl: {
        projection: 'orthographic',
      },
      light: {
        main: {
          intensity: 1.2,
        },
        ambient: {
          intensity: 0.3,
        },
      },
    },
    series: [
      {
        type: 'scatter3D',
        data: scatter3DData,
        symbolSize: 8,
        itemStyle: {
          opacity: 0.8,
        },
        emphasis: {
          itemStyle: {
            color: echartsColors.primary,
          },
        },
      },
    ],
  };

  // Radar: Skills Assessment
  const radarOption = {
    tooltip: {},
    legend: {
      data: ['Current Cohort', 'Previous Cohort'],
      bottom: 10,
    },
    radar: {
      indicator: [
        { name: 'Programming', max: 100 },
        { name: 'Problem Solving', max: 100 },
        { name: 'Collaboration', max: 100 },
        { name: 'Communication', max: 100 },
        { name: 'Time Management', max: 100 },
        { name: 'Creativity', max: 100 },
      ],
      shape: 'circle',
      splitNumber: 5,
      axisName: {
        color: '#666',
      },
      splitLine: {
        lineStyle: {
          color: alpha('#000', 0.1),
        },
      },
      splitArea: {
        show: true,
        areaStyle: {
          color: [
            alpha(echartsColors.primary, 0.05),
            alpha(echartsColors.primary, 0.1),
          ],
        },
      },
      axisLine: {
        lineStyle: {
          color: alpha('#000', 0.2),
        },
      },
    },
    series: [
      {
        name: 'Skills Assessment',
        type: 'radar',
        data: [
          {
            value: [85, 78, 82, 75, 88, 80],
            name: 'Current Cohort',
            areaStyle: {
              color: alpha(echartsColors.primary, 0.3),
            },
            lineStyle: {
              color: echartsColors.primary,
              width: 2,
            },
            itemStyle: {
              color: echartsColors.primary,
            },
          },
          {
            value: [78, 72, 75, 70, 80, 72],
            name: 'Previous Cohort',
            areaStyle: {
              color: alpha(echartsColors.secondary, 0.2),
            },
            lineStyle: {
              color: echartsColors.secondary,
              width: 2,
            },
            itemStyle: {
              color: echartsColors.secondary,
            },
          },
        ],
      },
    ],
  };

  // Candlestick: Weekly Performance Variations
  const candlestickData = [
    ['Week 1', 72, 85, 68, 82],
    ['Week 2', 82, 88, 78, 86],
    ['Week 3', 86, 90, 82, 85],
    ['Week 4', 85, 92, 81, 90],
    ['Week 5', 90, 94, 86, 91],
    ['Week 6', 91, 95, 88, 93],
  ];

  const candlestickOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
    },
    legend: {
      data: ['Performance Range'],
      bottom: 10,
    },
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
      top: '10%',
    },
    xAxis: {
      type: 'category',
      data: candlestickData.map(item => item[0]),
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      min: 60,
      max: 100,
      splitLine: {
        lineStyle: {
          type: 'dashed',
        },
      },
    },
    series: [
      {
        name: 'Performance Range',
        type: 'candlestick',
        data: candlestickData.map(item => item.slice(1)),
        itemStyle: {
          color: echartsColors.success,
          color0: echartsColors.danger,
          borderColor: echartsColors.success,
          borderColor0: echartsColors.danger,
        },
      },
    ],
  };

  // Heatmap: Activity by day and hour
  const hours = [
    '0h', '2h', '4h', '6h', '8h', '10h',
    '12h', '14h', '16h', '18h', '20h', '22h',
  ];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const heatmapData = [];
  for (let i = 0; i < days.length; i++) {
    for (let j = 0; j < hours.length; j++) {
      heatmapData.push([j, i, Math.floor(Math.random() * 100)]);
    }
  }

  const heatmapOption = {
    tooltip: {
      position: 'top',
      formatter: function (params) {
        return `${days[params.value[1]]} ${hours[params.value[0]]}<br/>Activity: ${params.value[2]}`;
      },
    },
    grid: {
      height: '75%',
      top: '8%',
      left: '10%',
    },
    xAxis: {
      type: 'category',
      data: hours,
      splitArea: {
        show: true,
      },
    },
    yAxis: {
      type: 'category',
      data: days,
      splitArea: {
        show: true,
      },
    },
    visualMap: {
      min: 0,
      max: 100,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: '5%',
      inRange: {
        color: [
          alpha(echartsColors.primary, 0.2),
          echartsColors.primary,
          echartsColors.danger,
        ],
      },
    },
    series: [
      {
        name: 'Activity',
        type: 'heatmap',
        data: heatmapData,
        label: {
          show: false,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  // Funnel: Student Journey
  const funnelOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {c}',
    },
    legend: {
      bottom: 10,
    },
    series: [
      {
        name: 'Student Journey',
        type: 'funnel',
        left: '10%',
        top: 60,
        bottom: 60,
        width: '80%',
        min: 0,
        max: 100,
        minSize: '0%',
        maxSize: '100%',
        sort: 'descending',
        gap: 2,
        label: {
          show: true,
          position: 'inside',
          formatter: '{b}: {c}%',
          fontSize: 14,
          color: '#fff',
        },
        labelLine: {
          length: 10,
          lineStyle: {
            width: 1,
            type: 'solid',
          },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        emphasis: {
          label: {
            fontSize: 16,
          },
        },
        data: [
          { value: 100, name: 'Enrolled', itemStyle: { color: echartsColors.chart[0] } },
          { value: 88, name: 'Started Course', itemStyle: { color: echartsColors.chart[1] } },
          { value: 75, name: 'Mid-term', itemStyle: { color: echartsColors.chart[2] } },
          { value: 68, name: 'Submitted Final', itemStyle: { color: echartsColors.chart[3] } },
          { value: 65, name: 'Completed', itemStyle: { color: echartsColors.success } },
        ],
      },
    ],
  };

  // Sunburst: Course Categories and Topics
  const sunburstOption = {
    tooltip: {
      trigger: 'item',
    },
    series: [
      {
        name: 'Course Structure',
        type: 'sunburst',
        data: [
          {
            name: 'Programming',
            value: 520,
            itemStyle: { color: echartsColors.chart[0] },
            children: [
              {
                name: 'Python',
                value: 280,
                itemStyle: { color: alpha(echartsColors.chart[0], 0.8) },
                children: [
                  { name: 'Basics', value: 120, itemStyle: { color: alpha(echartsColors.chart[0], 0.6) } },
                  { name: 'Advanced', value: 160, itemStyle: { color: alpha(echartsColors.chart[0], 0.7) } },
                ],
              },
              {
                name: 'JavaScript',
                value: 240,
                itemStyle: { color: alpha(echartsColors.chart[0], 0.6) },
                children: [
                  { name: 'Frontend', value: 140, itemStyle: { color: alpha(echartsColors.chart[0], 0.4) } },
                  { name: 'Backend', value: 100, itemStyle: { color: alpha(echartsColors.chart[0], 0.5) } },
                ],
              },
            ],
          },
          {
            name: 'Data Science',
            value: 380,
            itemStyle: { color: echartsColors.chart[1] },
            children: [
              {
                name: 'Statistics',
                value: 180,
                itemStyle: { color: alpha(echartsColors.chart[1], 0.8) },
              },
              {
                name: 'ML',
                value: 200,
                itemStyle: { color: alpha(echartsColors.chart[1], 0.6) },
                children: [
                  { name: 'Supervised', value: 120, itemStyle: { color: alpha(echartsColors.chart[1], 0.5) } },
                  { name: 'Unsupervised', value: 80, itemStyle: { color: alpha(echartsColors.chart[1], 0.4) } },
                ],
              },
            ],
          },
          {
            name: 'Design',
            value: 280,
            itemStyle: { color: echartsColors.chart[2] },
            children: [
              {
                name: 'UX',
                value: 160,
                itemStyle: { color: alpha(echartsColors.chart[2], 0.8) },
              },
              {
                name: 'UI',
                value: 120,
                itemStyle: { color: alpha(echartsColors.chart[2], 0.6) },
              },
            ],
          },
        ],
        radius: ['20%', '90%'],
        itemStyle: {
          borderRadius: 7,
          borderWidth: 2,
          borderColor: '#fff',
        },
        label: {
          fontSize: 12,
          rotate: 'radial',
        },
      },
    ],
  };

  // Parallel: Multi-dimensional Student Analysis
  const parallelOption = {
    tooltip: {
      trigger: 'item',
    },
    parallelAxis: [
      { dim: 0, name: 'Attendance', min: 0, max: 100 },
      { dim: 1, name: 'Assignments', min: 0, max: 100 },
      { dim: 2, name: 'Participation', min: 0, max: 100 },
      { dim: 3, name: 'Exam', min: 0, max: 100 },
      { dim: 4, name: 'Final Grade', min: 0, max: 100 },
    ],
    parallel: {
      left: '5%',
      right: '5%',
      bottom: 60,
      top: 60,
      parallelAxisDefault: {
        type: 'value',
        nameLocation: 'end',
        nameGap: 20,
        nameTextStyle: {
          fontSize: 12,
        },
      },
    },
    series: [
      {
        name: 'Student Performance',
        type: 'parallel',
        lineStyle: {
          width: 2,
          opacity: 0.5,
        },
        data: [
          [88, 85, 90, 92, 89],
          [75, 72, 78, 80, 76],
          [92, 88, 95, 90, 91],
          [65, 68, 70, 72, 68],
          [82, 80, 85, 88, 84],
          [70, 75, 72, 78, 74],
          [95, 92, 98, 94, 94],
        ],
      },
    ],
  };

  // Word Cloud: Educational Analytics Terms
  const wordCloudData = [
    { name: 'Analytics', value: 450 },
    { name: 'Engagement', value: 380 },
    { name: 'Conversion', value: 420 },
    { name: 'Retention', value: 350 },
    { name: 'Dashboard', value: 320 },
    { name: 'Funnel', value: 290 },
    { name: 'Customer', value: 400 },
    { name: 'Journey', value: 310 },
    { name: 'Metrics', value: 360 },
    { name: 'Performance', value: 340 },
    { name: 'Behavior', value: 280 },
    { name: 'Optimization', value: 300 },
    { name: 'Segmentation', value: 270 },
    { name: 'Data', value: 390 },
    { name: 'Insights', value: 330 },
    { name: 'Experience', value: 310 },
    { name: 'Cohort', value: 260 },
    { name: 'Attribution', value: 250 },
    { name: 'Touchpoint', value: 240 },
    { name: 'KPI', value: 280 },
    { name: 'ROI', value: 270 },
    { name: 'Value', value: 290 },
    { name: 'Scoring', value: 230 },
    { name: 'Qualification', value: 220 },
    { name: 'Proposition', value: 210 },
    { name: 'Scale', value: 240 },
    { name: 'Velocity', value: 220 },
    { name: 'Nurturing', value: 250 },
    { name: 'Churn', value: 260 },
    { name: 'Loyalty', value: 270 },
    { name: 'Advocacy', value: 200 },
    { name: 'Touchpoints', value: 240 },
    { name: 'Interaction', value: 230 },
    { name: 'Positioning', value: 210 },
    { name: 'Response', value: 220 },
    { name: 'Targeting', value: 230 },
    { name: 'Messaging', value: 210 },
    { name: 'Benchmark', value: 220 },
    { name: 'Leads', value: 260 },
    { name: 'Revenue', value: 280 },
    { name: 'Growth', value: 300 },
    { name: 'Pipeline', value: 270 },
    { name: 'Onboarding', value: 240 },
    { name: 'Satisfaction', value: 250 },
    { name: 'Strategy', value: 260 },
    { name: 'Channels', value: 230 },
    { name: 'Automation', value: 270 },
    { name: 'Acquisition', value: 240 },
    { name: 'Feedback', value: 230 },
    { name: 'Lifecycle', value: 220 },
  ];

  const wordCloudOption = {
    tooltip: {
      show: true,
    },
    series: [{
      type: 'wordCloud',
      shape: 'circle',
      left: 'center',
      top: 'center',
      width: '90%',
      height: '90%',
      right: null,
      bottom: null,
      sizeRange: [14, 60],
      rotationRange: [-45, 45],
      rotationStep: 15,
      gridSize: 8,
      drawOutOfBound: false,
      layoutAnimation: true,
      textStyle: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 'bold',
        color: function () {
          const colors = [
            echartsColors.primary,
            echartsColors.secondary,
            echartsColors.success,
            echartsColors.warning,
            echartsColors.purple,
            echartsColors.danger,
            echartsColors.orange,
            echartsColors.pink,
            echartsColors.teal,
            echartsColors.indigo,
          ];
          return colors[Math.floor(Math.random() * colors.length)];
        },
      },
      emphasis: {
        focus: 'self',
        textStyle: {
          textShadowBlur: 10,
          textShadowColor: '#333',
        },
      },
      data: wordCloudData,
    }],
  };

  // Theme River: Enrollment Trends by Track
  const themeRiverOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: 'rgba(0,0,0,0.2)',
          width: 1,
          type: 'solid',
        },
      },
    },
    legend: {
      data: ['Python Track', 'Web Dev Track', 'Data Science Track', 'UX Track'],
      bottom: 10,
    },
    singleAxis: {
      top: 50,
      bottom: 80,
      axisTick: {},
      axisLabel: {},
      type: 'time',
      axisPointer: {
        animation: true,
        label: {
          show: true,
        },
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.2,
        },
      },
    },
    series: [
      {
        type: 'themeRiver',
        emphasis: {
          itemStyle: {
            shadowBlur: 20,
            shadowColor: 'rgba(0, 0, 0, 0.8)',
          },
        },
        data: [
          ['2024-01', 80, 'Python Track'],
          ['2024-02', 90, 'Python Track'],
          ['2024-03', 100, 'Python Track'],
          ['2024-04', 110, 'Python Track'],
          ['2024-05', 95, 'Python Track'],
          ['2024-06', 105, 'Python Track'],
          ['2024-01', 60, 'Web Dev Track'],
          ['2024-02', 70, 'Web Dev Track'],
          ['2024-03', 85, 'Web Dev Track'],
          ['2024-04', 95, 'Web Dev Track'],
          ['2024-05', 88, 'Web Dev Track'],
          ['2024-06', 92, 'Web Dev Track'],
          ['2024-01', 40, 'Data Science Track'],
          ['2024-02', 50, 'Data Science Track'],
          ['2024-03', 55, 'Data Science Track'],
          ['2024-04', 65, 'Data Science Track'],
          ['2024-05', 70, 'Data Science Track'],
          ['2024-06', 75, 'Data Science Track'],
          ['2024-01', 30, 'UX Track'],
          ['2024-02', 35, 'UX Track'],
          ['2024-03', 42, 'UX Track'],
          ['2024-04', 48, 'UX Track'],
          ['2024-05', 52, 'UX Track'],
          ['2024-06', 58, 'UX Track'],
        ],
      },
    ],
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: { xs: 2, md: 4 }
    }}>
      {/* Hero Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: `linear-gradient(135deg, ${echartsColors.primary} 0%, ${echartsColors.purple} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Apache ECharts Showcase
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400, mb: 3 }}>
          Powerful, interactive charting library - Educational Analytics with 3D, Gauges & Advanced Visualizations
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="3D Scatter" color="primary" />
          <Chip label="Gauges" sx={{ bgcolor: echartsColors.secondary, color: 'white' }} />
          <Chip label="Heatmaps" sx={{ bgcolor: echartsColors.success, color: 'white' }} />
          <Chip label="Radar Charts" sx={{ bgcolor: echartsColors.warning, color: 'white' }} />
          <Chip label="Sunburst" sx={{ bgcolor: echartsColors.purple, color: 'white' }} />
          <Chip label="Parallel Coords" sx={{ bgcolor: echartsColors.danger, color: 'white' }} />
        </Box>
      </Box>

      {/* Section 1: Gauges & KPIs */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Performance Gauges & KPIs
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Overall Satisfaction"
            subtitle="Net Promoter Score (NPS)"
            icon={GaugeIcon}
            gradientColors={echartsColors.gradient.blue}
          >
            <ReactECharts option={gaugeOption} style={{ height: '350px' }} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Dual KPIs Dashboard"
            subtitle="Completion rate & engagement metrics"
            icon={GaugeIcon}
            gradientColors={echartsColors.gradient.forest}
          >
            <ReactECharts option={multiGaugeOption} style={{ height: '350px' }} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Course Progress"
            subtitle="Circular progress indicator"
            icon={LiquidIcon}
            gradientColors={echartsColors.gradient.ocean}
          >
            <ReactECharts option={liquidOption} style={{ height: '350px' }} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 2: 3D Visualizations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        3D Visualizations
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="3D Scatter: Student Performance Analysis"
            subtitle="Three-dimensional view of attendance, assignments, and exam scores"
            icon={Scatter3DIcon}
            gradientColors={[echartsColors.pink, echartsColors.purple]}
          >
            <ReactECharts 
              option={scatter3DOption} 
              style={{ height: '500px' }}
              opts={{ renderer: 'canvas' }}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 3: Advanced Charts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Multi-Dimensional Analysis
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Skills Radar Assessment"
            subtitle="Comprehensive skills comparison across cohorts"
            icon={RadarIcon}
            gradientColors={echartsColors.gradient.blue}
          >
            <ReactECharts option={radarOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Weekly Performance Variations"
            subtitle="Min, max, open, close performance metrics"
            icon={CandlestickIcon}
            gradientColors={echartsColors.gradient.sunset}
          >
            <ReactECharts option={candlestickOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 4: Heatmap & Patterns */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Activity Patterns & Density
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Weekly Activity Heatmap"
            subtitle="Student platform activity by day and hour"
            icon={HeatmapIcon}
            gradientColors={[echartsColors.indigo, echartsColors.teal]}
          >
            <ReactECharts option={heatmapOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 5: Hierarchical & Flow */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Hierarchical & Flow Visualizations
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Student Journey Funnel"
            subtitle="Progression through course stages"
            icon={TimelineIcon}
            gradientColors={[echartsColors.orange, echartsColors.danger]}
          >
            <ReactECharts option={funnelOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Course Structure Sunburst"
            subtitle="Hierarchical view of courses, tracks, and topics"
            icon={DonutIcon}
            gradientColors={[echartsColors.success, echartsColors.teal]}
          >
            <ReactECharts option={sunburstOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 6: Advanced Layouts */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Advanced Layouts & Comparisons
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Parallel Coordinates"
            subtitle="Multi-dimensional student performance comparison"
            icon={GraphIcon}
            gradientColors={echartsColors.gradient.blue}
          >
            <ReactECharts option={parallelOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Theme River: Enrollment Trends"
            subtitle="Track popularity over time (stacked stream)"
            icon={LineIcon}
            gradientColors={echartsColors.gradient.forest}
          >
            <ReactECharts option={themeRiverOption} style={{ height: '400px' }} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Section 7: Word Cloud */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Text Analytics & Semantic Visualization
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <ChartCard
            title="Educational Analytics Word Cloud"
            subtitle="Most frequently used terms in analytics, engagement, and performance metrics"
            icon={DonutIcon}
            gradientColors={[echartsColors.pink, echartsColors.purple]}
          >
            <ReactECharts option={wordCloudOption} style={{ height: '500px' }} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Footer info */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', bgcolor: alpha('#fff', 0.9), borderRadius: 3, border: `1px solid ${alpha(echartsColors.primary, 0.1)}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: echartsColors.primary }}>
          📊 Apache ECharts
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Enterprise-grade visualization library with 3D support, WebGL rendering, and 20+ chart types.
          Perfect for complex educational analytics with advanced interactivity.
          All data shown is synthetic and generated for demonstration purposes only.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
          <Chip label="3D Support" size="small" color="primary" />
          <Chip label="WebGL Rendering" size="small" sx={{ bgcolor: echartsColors.success, color: 'white' }} />
          <Chip label="20+ Chart Types" size="small" sx={{ bgcolor: echartsColors.warning, color: 'white' }} />
          <Chip label="Enterprise Ready" size="small" sx={{ bgcolor: echartsColors.purple, color: 'white' }} />
          <Chip label="Open Source" size="small" sx={{ bgcolor: echartsColors.secondary, color: 'white' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default EChartsShowcase;

